import * as React from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  getPaginationRowModel,
  ColumnDef,
} from '@tanstack/react-table';
import { cn } from '@/utils/cn';
import { useRef } from 'react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePaginationStore } from '@/store/use-pagination-store';
import useViewport from '@/hooks/use-viewport';

interface TableProps<T> {
  data: T[];
  columns: ColumnDef<T>[];
  onRowClick?: (row: T) => void;
  initialPageSize?: number;
  loading?: boolean;
  className?: string;
  isMarketPage?: boolean;
  getRowClassName?: (row: T) => string;
}

export const Table = <T,>({
  data,
  columns,
  onRowClick,
  loading = false,
  className,
  isMarketPage = false,
  getRowClassName,
}: TableProps<T>) => {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const { pagination, setPagination } = usePaginationStore();
  const [isDelayed, setIsDelayed] = React.useState<boolean>(false);
  const viewport = useViewport();
  const isMobile = viewport == 'sm';

  React.useEffect(() => {
    setPagination({
      pageSize: isMobile ? (isMarketPage ? 50 : 5) : 50,
      pageIndex: 0,
    });
  }, [isMobile, isMarketPage]);

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      pagination,
    },
    onPaginationChange: setPagination,
    autoResetPageIndex: false,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  React.useEffect(() => {
    setTimeout(() => setIsDelayed(true), 300);
  }, []);

  const isLoading = !isDelayed || loading;

  React.useEffect(() => {
    if (pagination.pageIndex + 1 > table.getPageCount())
      setPagination((prev) => ({
        ...prev,
        pageIndex: 0,
      }));
  }, [pagination.pageSize, data]);

  return (
    <div
      className={cn(
        'no-scrollbar h-full max-h-full w-full overflow-y-auto',
        className,
      )}
    >
      <table className="my-auto w-full md:table-fixed">
        <thead className="sticky top-0 z-10">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr
              key={headerGroup.id}
              className="bg-background capitalize text-foreground"
            >
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  className={cn(
                    'border-b-2 border-border px-1 py-3 text-left font-mono text-sm font-normal uppercase first:pl-6 last:pr-6 sm:first:pl-8 sm:last:pr-8 md:py-4',
                    // @ts-ignore
                    header.column.columnDef.meta?.width,
                  )}
                >
                  {header.isPlaceholder ? null : (
                    <div
                      {...{
                        className: header.column.getCanSort()
                          ? 'cursor-pointer select-none flex min-w-[36px]'
                          : '',
                        onClick: header.column.getToggleSortingHandler(),
                      }}
                    >
                      {flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                      {{
                        asc: <span className="pl-2">↑</span>,
                        desc: <span className="pl-2">↓</span>,
                      }[header.column.getIsSorted() as string] ?? null}
                    </div>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className="before:table-row before:h-2 after:table-row after:h-2">
          {isLoading &&
            [...Array(3)].map((_, i) => (
              <tr key={i}>
                {table.getAllColumns().map((col, i) => (
                  <td
                    key={i}
                    className={cn(
                      'group/skeleton px-1 py-2 first:pl-6 last:pr-6',
                      //@ts-ignore
                      col.columnDef.meta?.width,
                    )}
                  >
                    <div className="h-4 w-full animate-pulse rounded bg-neutral-950 max-md:group-last/skeleton:ml-auto md:w-[80%]" />
                  </td>
                ))}
              </tr>
            ))}
          {!isLoading &&
            data.length > 0 &&
            table.getRowModel().rows.map((row) => (
              <tr
                key={row.id}
                onClick={
                  onRowClick ? () => onRowClick(row.original) : undefined
                }
                className={cn(
                  'group bg-background font-mono',
                  getRowClassName && getRowClassName(row.original),
                )}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={cn(
                      'h-12 p-1 text-foreground first:pl-6 last:pr-6 group-hover:bg-[#FF5A44]/5 sm:first:pl-8 sm:last:pr-8',
                      // @ts-ignore
                      cell.column.columnDef.meta?.width,
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
        </tbody>
      </table>
      {data.length > 5 && (
        <div className="absolute bottom-0 z-20 h-12 w-full flex-col items-center justify-center gap-5 border-y border-border font-mono text-sm text-vestgrey-100 md:flex">
          <SelectGroup className="left-0 hidden h-full w-full items-center justify-center md:absolute md:flex md:max-w-sm md:justify-start md:pl-9">
            <SelectLabel className="font-mono text-sm font-normal text-vestgrey-100">
              ROWS PER PAGE:
            </SelectLabel>
            <Select onValueChange={(value) => table.setPageSize(Number(value))}>
              <SelectTrigger
                id="per-page"
                className="h-full max-w-14 rounded-none border-y-0 text-vestgrey-100"
              >
                {table.getState().pagination.pageSize}
              </SelectTrigger>
              <SelectContent className="min-w-0 max-w-14 font-mono">
                {[5, 10, 20, 50].map((size) => (
                  <SelectItem
                    key={size}
                    value={`${size}`}
                    className="text-vestgrey-100"
                  >
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </SelectGroup>
          <div className="flex h-full w-full items-center justify-center">
            <button
              className="h-full px-3 hover:bg-vestgrey-800 hover:text-primary disabled:pointer-events-none"
              onClick={() => table.firstPage()}
              disabled={!table.getCanPreviousPage()}
            >
              {'<<'}
            </button>
            <button
              className="h-full px-3 hover:bg-vestgrey-800 hover:text-primary disabled:pointer-events-none"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              {'< PREV'}
            </button>
            <span className="flex h-full items-center px-4">
              PAGE {table.getState().pagination.pageIndex + 1} OF{' '}
              {table.getPageCount()}
            </span>
            <button
              className="h-full px-3 hover:bg-vestgrey-800 hover:text-primary disabled:pointer-events-none"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              {'NEXT >'}
            </button>
            <button
              className="h-full px-3 hover:bg-vestgrey-800 hover:text-primary disabled:pointer-events-none"
              onClick={() => table.lastPage()}
              disabled={!table.getCanNextPage()}
            >
              {'>>'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
